import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'react-bootstrap';
import { FaBars, FaRobot, FaChevronRight } from 'react-icons/fa';
import { setSelectedStore, setSelectedClient } from '../../redux/actions';
import { selectViewMode } from '../../redux/selectors';
import './index.scss';

const MobileBottomNav = ({ onMenuToggle, sbOpen, onToggleSb }) => {
  const dispatch = useDispatch();
  const { stores, selectedStore } = useSelector((state) => state.stores);
  const { clients, selectedClient } = useSelector((state) => state.clients);
  const user = useSelector((state) => state.user);
  const viewMode = useSelector(selectViewMode);

  const isOwnerView =
    user?.role === 'Admin' || (user?.role === 'Super Admin' && viewMode === 'owner');

  const showStoreSelector = stores.length > 0;

  // Track which store is "expanded" in the combined picker
  const [expandedStoreId, setExpandedStoreId] = useState(null);
  const [dualOpen, setDualOpen] = useState(false);
  const dualRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!dualOpen) return;
    const handler = (e) => {
      if (dualRef.current && !dualRef.current.contains(e.target)) {
        setDualOpen(false);
        setExpandedStoreId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dualOpen]);

  const handleSelectStore = (store) => {
    dispatch(setSelectedStore(store));
    dispatch(setSelectedClient(null));
    setExpandedStoreId(null);
    setDualOpen(false);
  };

  const handleSelectClient = (client) => {
    dispatch(setSelectedClient(client));
    setDualOpen(false);
    setExpandedStoreId(null);
  };

  const handleSelectAllClients = () => {
    dispatch(setSelectedClient(null));
    setDualOpen(false);
    setExpandedStoreId(null);
  };

  const toggleExpanded = (storeId, e) => {
    e.stopPropagation();
    setExpandedStoreId((prev) => (prev === storeId ? null : storeId));
  };

  // Combined label for the owner dual picker
  const dualLabel = selectedClient
    ? `${selectedStore?.name || 'Store'} · ${selectedClient.name}`
    : selectedStore?.name || 'Select store';

  return (
    <div className="mobile-bottom-nav">
      {/* Selector zone */}
      <div className="mobile-bottom-nav__selectors">
        {isOwnerView && showStoreSelector ? (
          // Owner view: combined store + client picker
          <div className="mobile-bottom-nav__dual-wrap" ref={dualRef}>
            <button
              className={`mobile-bottom-nav__selector${dualOpen ? ' show' : ''}`}
              onClick={() => {
                setDualOpen((v) => !v);
                if (!expandedStoreId && stores.length === 1) {
                  setExpandedStoreId(stores[0].storeId);
                }
              }}
              aria-haspopup="true"
              aria-expanded={dualOpen}>
              <span className="mobile-bottom-nav__selector-label">{dualLabel}</span>
            </button>

            {dualOpen && (
              <div className="mobile-bottom-nav__dual-menu">
                {stores.map((store) => (
                  <div key={store.storeId} className="mobile-bottom-nav__dual-store-group">
                    <div
                      className={`mobile-bottom-nav__dual-store-row${selectedStore?.storeId === store.storeId ? ' is-selected' : ''}`}
                      onClick={() =>
                        clients.length > 0
                          ? toggleExpanded(store.storeId, { stopPropagation: () => {} })
                          : handleSelectStore(store)
                      }>
                      <button
                        className="mobile-bottom-nav__dual-store-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectStore(store);
                        }}>
                        {store.name}
                      </button>
                      {clients.length > 0 && (
                        <button
                          className={`mobile-bottom-nav__dual-expand-btn${expandedStoreId === store.storeId ? ' is-open' : ''}`}
                          onClick={(e) => toggleExpanded(store.storeId, e)}
                          aria-label={
                            expandedStoreId === store.storeId
                              ? 'Collapse clients'
                              : 'Expand clients'
                          }>
                          <FaChevronRight size={10} />
                        </button>
                      )}
                    </div>

                    {expandedStoreId === store.storeId && (
                      <div className="mobile-bottom-nav__dual-clients">
                        <button
                          className={`mobile-bottom-nav__dual-client-btn${!selectedClient ? ' is-selected' : ''}`}
                          onClick={handleSelectAllClients}>
                          All Clients
                        </button>
                        {clients.map((c) => (
                          <button
                            key={c.id}
                            className={`mobile-bottom-nav__dual-client-btn${selectedClient?.id === c.id ? ' is-selected' : ''}`}
                            onClick={() => {
                              dispatch(setSelectedStore(store));
                              handleSelectClient(c);
                            }}>
                            {c.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          // Admin / Super Admin view: plain store selector
          showStoreSelector && (
            <Dropdown drop="up">
              <Dropdown.Toggle variant="" className="mobile-bottom-nav__selector">
                <span className="mobile-bottom-nav__selector-label">
                  {selectedStore?.name || 'Select store'}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="mobile-bottom-nav__menu">
                {stores.map((s) => (
                  <Dropdown.Item
                    key={s.storeId}
                    as="button"
                    onClick={() => dispatch(setSelectedStore(s))}
                    active={selectedStore?.storeId === s.storeId}>
                    {s.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )
        )}
      </div>

      {/* Right controls: SavvyBot + hamburger */}
      <div className="mobile-bottom-nav__right">
        {onToggleSb && (
          <button
            className={`mobile-bottom-nav__bot-btn${sbOpen ? ' mobile-bottom-nav__bot-btn--active' : ''}`}
            onClick={onToggleSb}
            aria-label={sbOpen ? 'Close SavvyBot' : 'Open SavvyBot'}
            aria-pressed={!!sbOpen}>
            <FaRobot size={20} />
          </button>
        )}
        <button
          className="mobile-bottom-nav__hamburger"
          onClick={onMenuToggle}
          aria-label="Open menu">
          <FaBars size={20} />
        </button>
      </div>
    </div>
  );
};

export default MobileBottomNav;
